import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Divider,
} from "@mui/material";
import {
  getAllUserPermissions,
  createOrUpdateUserPermission,
  deleteUserPermission,
  resetUserPermissions,
} from "../../services/userPermission.service";
import { getClient } from "../../services/client.service";
import { getAllUsers } from "../../services/user.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import Styles from "../Employee/Employee.module.scss";
import {
  superAdminMenu,
  userMenu,
  clientMenu,
  ManagerMenu,
  pathToPermissionMap,
} from "../Home/Menu";

const THEME_COLOR = "#008080";

const PermissionManagement = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchClients();
    fetchModules();
  }, []);

  useEffect(() => {
    const type = tabValue === 0 ? "employee" : "client";
    getData(type);
  }, [tabValue]);

  const getData = async (type) => {
    try {
      const res = await getAllUserPermissions({ type });
      const responseData = res?.data || [];
      const modifiedData = responseData.map((e) => ({
        ...e,
        userName: e.userId
          ? e.userId.name || `${e.userId.firstName} ${e.userId.lastName}`
          : "N/A",
        employeeID: e.userId?.employeeID || "N/A",
        email: e.userId?.email || "N/A",
        role: e.userId?.role,
        permissionCount: countPermissions(e.permissions),
      }));
      setData(modifiedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      ToastError("Failed to fetch permissions");
      setData([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const query = { isAdmin: "false", page: "1", limit: "1000" };
      const res = await getAllUsers(query);
      setUsers(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const query = { page: 1, limit: 1000 };
      const res = await getClient(query);
      setClients(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchModules = () => {
    const allMenus = [
      ...superAdminMenu,
      ...userMenu,
      ...clientMenu,
      ...ManagerMenu,
    ];

    const uniqueModules = new Map();

    const processMenu = (menuItems) => {
      menuItems.forEach((item) => {
        if (item.path && pathToPermissionMap[item.path]) {
          const permissionKey = pathToPermissionMap[item.path];
          if (!uniqueModules.has(permissionKey)) {
            uniqueModules.set(permissionKey, {
              key: permissionKey,
              label: item.title,
            });
          }
        }
        if (item.subMenu && item.subMenu.length > 0) {
          processMenu(item.subMenu);
        }
      });
    };

    processMenu(allMenus);
    const dynamicModules = Array.from(uniqueModules.values());
    console.log("Modules extracted from Menu.js:", dynamicModules);
    setModules(dynamicModules);
  };

  const countPermissions = (perms) => {
    if (!perms) return 0;
    let count = 0;
    Object.values(perms).forEach((perm) => {
      if (perm.view) count++;
      if (perm.add) count++;
      if (perm.edit) count++;
      if (perm.delete) count++;
    });
    return count;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedUser(null);
  };

  const getFilteredUsers = () => {
    if (tabValue === 0) {
      return users.filter((u) => u.role !== 3);
    } else {
      return clients;
    }
  };

  const handleAdd = () => {
    setSelectedUser(null);
    const initialPermissions = {};
    modules.forEach((module) => {
      initialPermissions[module.key] = {
        view: false,
        add: false,
        edit: false,
        delete: false,
      };
    });
    setPermissions(initialPermissions);
    setOpenDialog(true);
  };

  const handleEdit = (row) => {
    setSelectedUser(row.userId);
    setPermissions(row.permissions || {});
    setOpenDialog(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete these permissions?")) {
      return;
    }
    try {
      const userId = row.userId?._id || row.userId;
      const res = await deleteUserPermission(userId);
      ToastSuccess(
        res?.data?.message || res?.message || "Permissions deleted successfully"
      );
      const type = tabValue === 0 ? "employee" : "client";
      getData(type);
    } catch (error) {
      ToastError(
        error?.response?.data?.message ||
        error?.data?.message ||
        "Failed to delete permissions"
      );
    }
  };

  const handlePermissionChange = (module, action) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module]?.[action],
      },
    }));
  };

  const handleSelectAll = (module) => {
    const allSelected =
      permissions[module]?.view &&
      permissions[module]?.add &&
      permissions[module]?.edit &&
      permissions[module]?.delete;

    setPermissions((prev) => ({
      ...prev,
      [module]: {
        view: !allSelected,
        add: !allSelected,
        edit: !allSelected,
        delete: !allSelected,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedUser) {
      ToastError("Please select a user");
      return;
    }

    setLoading(true);
    try {
      const userModel = tabValue === 0 ? "users" : "client";
      const res = await createOrUpdateUserPermission(
        selectedUser._id,
        permissions,
        userModel
      );
      ToastSuccess(res.message);
      setOpenDialog(false);
      const type = tabValue === 0 ? "employee" : "client";
      getData(type);
    } catch (error) {
      ToastError(error?.data?.message || "Failed to save permissions");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      id: "userName",
      label: tabValue === 0 ? "Employee Name" : "Client Name",
      minWidth: 200,
      align: "left",
    },
    ...(tabValue === 0
      ? [
        {
          id: "employeeID",
          label: "Employee ID",
          minWidth: 120,
          align: "left",
        },
      ]
      : []),
    {
      id: "email",
      label: "Email",
      minWidth: 200,
      align: "left",
    },
    {
      id: "permissionCount",
      label: "Permissions Set",
      minWidth: 120,
      align: "center",
    },
  ];

  const tableConfig = {
    isAction: true,
    isEdit: true,
    isDelete: true,
    isServerSide: false,
    isMore: true,
    isAdmin: true,
  };

  return (
    <Box className={Styles.Page}>
      <Box className={Styles.pageHeader} sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
                color: "#666",
              },
              "& .Mui-selected": {
                color: `${THEME_COLOR} !important`,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: THEME_COLOR,
              },
            }}
          >
            <Tab label="Employee" />
            <Tab label="Client" />
          </Tabs>

          <Button
            variant="contained"
            onClick={handleAdd}
            size="large"
            sx={{
              textTransform: "capitalize",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 2,
              bgcolor: THEME_COLOR,
              "&:hover": { bgcolor: "#006666" },
            }}
          >
            Add {tabValue === 0 ? "Employee" : "Client"} Permission
          </Button>
        </Box>

        <Divider sx={{ mt: 2 }} />
      </Box>

      <MasterCommonTable
        tableConfig={tableConfig}
        columns={columns}
        data={data}
        tableTotalCount={data.length}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: "600px" },
        }}
      >
        <DialogTitle sx={{ bgcolor: THEME_COLOR, color: "white", py: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedUser ? "Edit" : "Assign"}{" "}
            {tabValue === 0 ? "Employee" : "Client"} Permissions
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {!selectedUser && (
            <Box sx={{ mb: 3 }}>
              <Typography className={Styles.inputLabel} sx={{ mb: 1, fontWeight: 700, fontSize: "14px", color: "#333" }}>
                Select {tabValue === 0 ? "Employee" : "Client"} <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                fullWidth
                options={getFilteredUsers()}
                getOptionLabel={(option) =>
                  option.name
                    ? `${option.name}`
                    : `${option.firstName} ${option.lastName} (${option.employeeID})`
                }
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={selectedUser}
                onChange={(e, newValue) => setSelectedUser(newValue)}
                ListboxProps={{
                  sx: {
                    '& .MuiAutocomplete-option': {
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: '14px',
                      color: '#333',
                      '&[aria-selected="true"]': {
                        bgcolor: `${THEME_COLOR} !important`,
                        color: 'white !important',
                        '&.Mui-focused': {
                          bgcolor: `${THEME_COLOR} !important`,
                        },
                      },
                    },
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select ${tabValue === 0 ? "Employee" : "Client"}`}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: "white",
                        '&.Mui-focused fieldset': {
                          borderColor: THEME_COLOR,
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}



          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Module Permissions
          </Typography>

          <Grid container spacing={2}>
            {modules.map((module) => (
              <Grid item xs={12} key={module.key}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "#f9f9f9",
                    border: "1px solid #eee",
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600, minWidth: "200px", color: "#333" }}
                  >
                    {module.label}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
                    {["view", "add", "edit", "delete"].map((action) => (
                      <FormControlLabel
                        key={action}
                        control={
                          <Checkbox
                            checked={permissions[module.key]?.[action] || false}
                            onChange={() =>
                              handlePermissionChange(module.key, action)
                            }
                            sx={{
                              color: "#ccc",
                              "&.Mui-checked": {
                                color: THEME_COLOR,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }}>
                            {action}
                          </Typography>
                        }
                      />
                    ))}
                    <Button
                      size="small"
                      onClick={() => handleSelectAll(module.key)}
                      sx={{
                        textTransform: "none",
                        color: THEME_COLOR,
                        fontWeight: 600,
                        ml: 2,
                      }}
                    >
                      Select All
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #eee" }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              textTransform: "none",
              color: "#666",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !selectedUser}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: THEME_COLOR,
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#006666",
                boxShadow: 2,
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#ffffff",
              },
            }}
          >
            {loading ? "Saving..." : "Save Permissions"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionManagement;
